import { useState, useEffect } from 'react';

interface CityStat {
  city: string;
  total: number;
  phone_count: number;
  pending_count: number;
  percentage: number;
}

interface CategoryStat {
  category: string;
  total: number;
  phone_count: number;
  pending_count: number;
  percentage: number;
}

interface ListingItem {
  id: string;
  name: string;
  slug: string;
  city: string;
  category: string;
  phone: string | null;
  address: string | null;
  view_count: number;
  updated_at: string | null;
  phone_checked: boolean;
  source: string;
}

interface PhoneStats {
  total_listings: number;
  phone_verified_count: number;
  pending_count: number;
  completion_percentage: number;
  hourly_rate: number;
  daily_rate: number;
  today_rate: number;
  daily_free_limit: number;
  city_stats: CityStat[];
  category_stats: CategoryStat[];
  recent_fixes: ListingItem[];
}

interface FeedResponse {
  items: ListingItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export default function PhoneEnrichmentAdmin() {
  const [stats, setStats] = useState<PhoneStats | null>(null);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  
  // Feed state
  const [statusTab, setStatusTab] = useState<'done' | 'pending' | 'all'>('done');
  const [feedItems, setFeedItems] = useState<ListingItem[]>([]);
  const [feedTotal, setFeedTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loadingFeed, setLoadingFeed] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/v1/listings/phone-stats');
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load phone stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchFeed = async (page = 1) => {
    setLoadingFeed(true);
    try {
      const params = new URLSearchParams({
        status_filter: statusTab,
        page: page.toString(),
        limit: '15'
      });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (selectedCity && selectedCity !== 'all') params.set('city', selectedCity);
      if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);

      const res = await fetch(`/api/v1/listings/phone-feed?${params.toString()}`);
      if (res.ok) {
        const data: FeedResponse = await res.json();
        setFeedItems(data.items);
        setFeedTotal(data.total);
        setCurrentPage(data.page);
        setTotalPages(data.total_pages);
      }
    } catch (err) {
      console.error('Failed to load phone feed:', err);
    } finally {
      setLoadingFeed(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchFeed(1);
  }, [statusTab, selectedCity, selectedCategory]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      fetchStats();
      fetchFeed(currentPage);
    }, 12000);
    return () => clearInterval(timer);
  }, [autoRefresh, statusTab, selectedCity, selectedCategory, currentPage]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchFeed(1);
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-xs">
              <span className="material-symbols-outlined text-2xl">call</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
                  Phone Numbers Fixing Monitor
                </h1>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                  ₹0 Cost Autonomous Daemon
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Real-time tracking of phone numbers being fixed across the 4.5L+ business listings database.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 text-xs font-semibold rounded-xl border flex items-center gap-1.5 transition cursor-pointer ${
              autoRefresh 
                ? 'bg-blue-50 border-blue-300 text-blue-700' 
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-blue-500 animate-pulse' : 'bg-slate-400'}`}></span>
            {autoRefresh ? 'Live Auto-Refresh (12s)' : 'Paused'}
          </button>

          <button
            onClick={() => { fetchStats(); fetchFeed(currentPage); }}
            disabled={loadingStats || loadingFeed}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh Now
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Verified Phone Numbers Done */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Numbers Fixed (Done)</span>
            <span className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
              <span className="material-symbols-outlined text-lg">check_circle</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900">
              {stats ? stats.phone_verified_count.toLocaleString() : '...'}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                {stats ? `${stats.completion_percentage}%` : '0%'}
              </span>
              <span className="text-[11px] text-slate-400">verified contact numbers</span>
            </div>
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-4 overflow-hidden">
            <div 
              className="bg-emerald-500 h-full rounded-full transition-all duration-1000"
              style={{ width: `${Math.max(1, stats ? stats.completion_percentage : 0)}%` }}
            ></div>
          </div>
        </div>

        {/* Card 2: Pending Listings */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pending Numbers</span>
            <span className="p-2 rounded-xl bg-amber-50 text-amber-600">
              <span className="material-symbols-outlined text-lg">pending</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900">
              {stats ? stats.pending_count.toLocaleString() : '...'}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                In Queue
              </span>
              <span className="text-[11px] text-slate-400">awaiting enrichment</span>
            </div>
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-4 overflow-hidden">
            <div 
              className="bg-amber-500 h-full rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(100, stats ? (stats.pending_count / stats.total_listings * 100) : 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Card 3: Fixed in Last 24 Hours */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fixed in Last 24h</span>
            <span className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <span className="material-symbols-outlined text-lg">bolt</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-blue-600 flex items-baseline gap-1">
              +{stats ? stats.daily_rate.toLocaleString() : '...'}
              <span className="text-xs font-medium text-slate-500">listings</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                ~{stats ? stats.hourly_rate : 0}/hr speed
              </span>
              <span className="text-[11px] text-slate-400">continuous progress</span>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-emerald-500">trending_up</span>
            Growing daily without any manual work
          </div>
        </div>

        {/* Card 4: Daily Safe Free Limit Status */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Free Safe Daily Limit</span>
            <span className="p-2 rounded-xl bg-purple-50 text-purple-600">
              <span className="material-symbols-outlined text-lg">shield</span>
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-extrabold text-slate-900 flex items-baseline gap-1">
              2,500
              <span className="text-xs font-medium text-slate-500">lookups/day</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
                100% Free Rate
              </span>
              <span className="text-[11px] text-slate-400">3.0s safe delay</span>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-purple-500">autorenew</span>
            Auto-resets daily at midnight
          </div>
        </div>
      </div>

      {/* Main Interactive Table Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        {/* Table Filter Header */}
        <div className="p-5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-50/50">
          {/* Status Tabs */}
          <div className="flex items-center gap-1 p-1 bg-slate-200/60 rounded-xl">
            <button
              onClick={() => { setStatusTab('done'); setCurrentPage(1); }}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer flex items-center gap-1.5 ${
                statusTab === 'done'
                  ? 'bg-white text-emerald-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="material-symbols-outlined text-sm">check_circle</span>
              Fixed Numbers ({stats ? stats.phone_verified_count.toLocaleString() : '...'})
            </button>
            <button
              onClick={() => { setStatusTab('pending'); setCurrentPage(1); }}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer flex items-center gap-1.5 ${
                statusTab === 'pending'
                  ? 'bg-white text-amber-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="material-symbols-outlined text-sm">schedule</span>
              Pending Queue ({stats ? stats.pending_count.toLocaleString() : '...'})
            </button>
            <button
              onClick={() => { setStatusTab('all'); setCurrentPage(1); }}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer flex items-center gap-1.5 ${
                statusTab === 'all'
                  ? 'bg-white text-blue-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="material-symbols-outlined text-sm">list</span>
              All Listings
            </button>
          </div>

          {/* Search and Filters */}
          <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search business or phone..."
                className="w-56 md:w-64 pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:border-blue-500"
              />
              <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-sm">search</span>
            </div>

            {/* City Filter */}
            <select
              value={selectedCity}
              onChange={(e) => { setSelectedCity(e.target.value); setCurrentPage(1); }}
              className="px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Cities</option>
              {stats?.city_stats.map((c) => (
                <option key={c.city} value={c.city}>{c.city} ({c.total})</option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              className="px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {stats?.category_stats.map((cat) => (
                <option key={cat.category} value={cat.category}>{cat.category}</option>
              ))}
            </select>

            <button
              type="submit"
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold cursor-pointer"
            >
              Filter
            </button>
          </form>
        </div>

        {/* Listings Feed Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-600">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">Business Name & Location</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Contact Number</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Last Updated</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingFeed ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-2xl animate-spin text-blue-500">progress_activity</span>
                      <span>Loading listings feed...</span>
                    </div>
                  </td>
                </tr>
              ) : feedItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    No listings match the current filters.
                  </td>
                </tr>
              ) : (
                feedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                    {/* Business Name */}
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900 text-sm max-w-md truncate">
                        {item.name}
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center gap-0.5">
                          <span className="material-symbols-outlined text-xs text-slate-400">location_on</span>
                          {item.city || 'India'}
                        </span>
                        {item.address && (
                          <span className="truncate max-w-xs text-slate-400">· {item.address}</span>
                        )}
                      </div>
                    </td>

                    {/* Category */}
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium bg-slate-100 text-slate-700">
                        {item.category}
                      </span>
                    </td>

                    {/* Contact Number */}
                    <td className="py-3.5 px-4">
                      {item.phone ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={`tel:${item.phone.replace(/\s+/g, '')}`}
                            className="font-mono text-emerald-700 font-bold bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded-lg border border-emerald-200 text-xs flex items-center gap-1 transition"
                          >
                            <span className="material-symbols-outlined text-xs text-emerald-600">call</span>
                            {item.phone}
                          </a>
                          <button
                            onClick={() => copyToClipboard(item.phone || '', item.id)}
                            title="Copy Phone"
                            className="p-1 text-slate-400 hover:text-slate-600 rounded transition cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-xs">
                              {copiedId === item.id ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium text-[11px] bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200">
                          <span className="material-symbols-outlined text-xs text-amber-500">pending</span>
                          Pending Lookup
                        </span>
                      )}
                    </td>

                    {/* Status Badge */}
                    <td className="py-3.5 px-4">
                      {item.phone ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          Verified Fixed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                          In Queue
                        </span>
                      )}
                    </td>

                    {/* Last Updated */}
                    <td className="py-3.5 px-4 text-slate-400 text-[11px]">
                      {item.updated_at ? new Date(item.updated_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '—'}
                    </td>

                    {/* View Listing Button */}
                    <td className="py-3.5 px-4 text-right">
                      <a
                        href={`/b/${item.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 rounded-xl font-bold text-xs transition active:scale-95 shadow-2xs border border-blue-200 hover:border-blue-600"
                      >
                        <span>View Listing</span>
                        <span className="material-symbols-outlined text-xs">open_in_new</span>
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 bg-slate-50/50">
          <div>
            Showing page <span className="font-bold text-slate-800">{currentPage}</span> of{' '}
            <span className="font-bold text-slate-800">{totalPages}</span> ({feedTotal.toLocaleString()} total listings)
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchFeed(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1 || loadingFeed}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition font-medium text-xs flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-xs">chevron_left</span>
              Previous
            </button>
            <button
              onClick={() => fetchFeed(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages || loadingFeed}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition font-medium text-xs flex items-center gap-1"
            >
              Next
              <span className="material-symbols-outlined text-xs">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Breakdown Grid: Cities & Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* City Coverage Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg material-symbols-outlined text-lg">location_city</span>
              <h2 className="font-bold text-slate-900 text-sm">Top Cities Phone Coverage</h2>
            </div>
            <span className="text-xs text-slate-400">By Database Volume</span>
          </div>

          <div className="space-y-3.5">
            {stats?.city_stats.map((c) => (
              <div key={c.city} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-800">{c.city}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-mono">
                      {c.phone_count.toLocaleString()} / {c.total.toLocaleString()}
                    </span>
                    <span className="font-bold text-blue-600 min-w-9 text-right">{c.percentage}%</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-500 h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(2, c.percentage)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Coverage Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg material-symbols-outlined text-lg">category</span>
              <h2 className="font-bold text-slate-900 text-sm">Top Categories Phone Coverage</h2>
            </div>
            <span className="text-xs text-slate-400">High Intent Commercial</span>
          </div>

          <div className="space-y-3.5">
            {stats?.category_stats.map((cat) => (
              <div key={cat.category} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-800">{cat.category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-mono">
                      {cat.phone_count.toLocaleString()} / {cat.total.toLocaleString()}
                    </span>
                    <span className="font-bold text-emerald-600 min-w-9 text-right">{cat.percentage}%</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.max(2, cat.percentage)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* VPS 24/7 Daemon Info Footer Card */}
      <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
            <span className="font-bold text-sm text-slate-100">Daemon Operating Status: Active 24/7 on VPS</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Daemon service <code className="text-emerald-400 bg-slate-800 px-1.5 py-0.5 rounded">zhasearch-phone-enricher.service</code> is continuously running on VPS IP <code className="text-blue-300">66.116.252.87</code> with zero API charges.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-400">Check live server logs:</span>
          <code className="bg-slate-800 text-blue-300 px-3 py-1.5 rounded-xl border border-slate-700 font-mono select-all">
            tail -f /opt/zhasearch-backend/enrichment.log
          </code>
        </div>
      </div>
    </div>
  );
}
